import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from './config';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'KAM',
    brand_id: 1,
    is_active: true,
    send_email: true
  });

  const [brands, setBrands] = useState([
    { id: 1, name: 'Reliance TV (Malpani)' },
    { id: 2, name: 'AI+' },
    { id: 3, name: 'BoAT' },
    { id: 4, name: 'HARMAN' }
  ]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      showMessage('error', 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_URL}/api/admin/users`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        showMessage('success', `User ${formData.email} created successfully`);
        setShowCreateForm(false);
        setFormData({
          email: '',
          password: '',
          name: '',
          role: 'KAM',
          brand_id: 1,
          is_active: true,
          send_email: true
        });
        fetchUsers();
      }
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.put(`${API_URL}/api/admin/users/${editingUser.id}`, {
        name: editingUser.name,
        email: editingUser.email,
        role: editingUser.role,
        brand_id: editingUser.brand_id,
        is_active: editingUser.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        showMessage('success', 'User updated successfully');
        setEditingUser(null);
        fetchUsers();
      }
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = prompt('Enter new password for user:');
    if (!newPassword) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/admin/users/${userId}/reset-password`, 
        { password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', 'Password reset successfully');
      }
    } catch (error) {
      showMessage('error', 'Failed to reset password');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/api/admin/users/${userId}/status`, 
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', `User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        fetchUsers();
      }
    } catch (error) {
      showMessage('error', 'Failed to update user status');
    }
  };

  const openEditModal = (user) => {
    setEditingUser({
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      brand_id: user.brand_id,
      is_active: user.is_active
    });
  };

  return (
    <div className="fade-in" style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>User Management</h1>
        <button 
          className="premium-button success"
          onClick={() => setShowCreateForm(!showCreateForm)}
          style={styles.createButton}
        >
          {showCreateForm ? '✕ Cancel' : '+ Create New User'}
        </button>
      </div>

      {/* Message Display */}
      {message.text && (
        <div className={`message-toast ${message.type}`} style={{ position: 'static', marginBottom: '20px' }}>
          {message.text}
        </div>
      )}

      {/* Create User Form */}
      {showCreateForm && (
        <div className="premium-card" style={styles.formCard}>
          <h3 style={styles.formTitle}>Create New User</h3>
          <form onSubmit={handleCreateUser} style={styles.form}>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="premium-input"
                  style={styles.input}
                  required
                  placeholder="John Doe"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="premium-input"
                  style={styles.input}
                  required
                  placeholder="user@example.com"
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="premium-input"
                  style={styles.input}
                  required
                  placeholder="••••••••"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="premium-select"
                  style={styles.input}
                >
                  <option value="KAM">KAM</option>
                  <option value="Admin">Admin</option>
                  <option value="Viewer">Viewer</option>
                </select>
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Brand Access</label>
                <select
                  value={formData.brand_id}
                  onChange={(e) => setFormData({...formData, brand_id: parseInt(e.target.value)})}
                  className="premium-select"
                  style={styles.input}
                >
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Send Welcome Email</label>
                <div style={styles.checkboxGroup}>
                  <input
                    type="checkbox"
                    checked={formData.send_email}
                    onChange={(e) => setFormData({...formData, send_email: e.target.checked})}
                    style={styles.checkbox}
                  />
                  <span style={styles.checkboxLabel}>Send credentials to user's email</span>
                </div>
              </div>
            </div>

            <div style={styles.formActions}>
              <button type="submit" className="premium-button success" disabled={loading}>
                {loading ? 'Creating...' : 'Create User'}
              </button>
              <button 
                type="button" 
                className="premium-button secondary"
                onClick={() => setShowCreateForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      <div className="premium-table-container" style={styles.tableCard}>
        <h3 style={styles.tableTitle}>All Users ({users.length})</h3>
        
        {loading ? (
          <div className="premium-loading">
            <div className="loading-spinner" />
          </div>
        ) : (
          <table className="premium-table" style={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Brand</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} style={styles.tableRow}>
                  <td>
                    <div style={styles.userInfo}>
                      <strong>{user.name}</strong>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span style={{
                      ...styles.roleBadge,
                      backgroundColor: user.role === 'Admin' ? '#FF3B3020' : 
                                     user.role === 'KAM' ? '#007AFF20' : '#8E8E9320',
                      color: user.role === 'Admin' ? '#FF3B30' : 
                             user.role === 'KAM' ? '#007AFF' : '#8E8E93'
                    }}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span style={styles.brandBadge}>
                      {brands.find(b => b.id === user.brand_id)?.name || 'All'}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: user.is_active ? '#34C75920' : '#FF3B3020',
                      color: user.is_active ? '#34C759' : '#FF3B30'
                    }}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(user.created_at).toLocaleDateString()}</td>
                  <td>{user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}</td>
                  <td>
                    <div style={styles.actionButtons}>
                      <button 
                        onClick={() => handleResetPassword(user.id)}
                        className="premium-button info"
                        style={styles.actionButton}
                        title="Reset Password"
                      >
                        🔑
                      </button>
                      <button 
                        onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                        className={`premium-button ${user.is_active ? 'warning' : 'success'}`}
                        style={styles.actionButton}
                        title={user.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {user.is_active ? '🔴' : '🟢'}
                      </button>
                      <button 
                        onClick={() => openEditModal(user)}
                        className="premium-button secondary"
                        style={styles.actionButton}
                        title="Edit User"
                      >
                        ✏️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <div style={styles.modalOverlay}>
          <div className="premium-card" style={styles.modal}>
            <h3 style={styles.modalTitle}>Edit User</h3>
            
            <div style={styles.modalContent}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Full Name</label>
                <input
                  type="text"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                  className="premium-input"
                  style={styles.modalInput}
                  placeholder="Enter full name"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  className="premium-input"
                  style={styles.modalInput}
                  placeholder="Enter email address"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Role</label>
                <select
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                  className="premium-select"
                  style={styles.modalInput}
                >
                  <option value="KAM">KAM</option>
                  <option value="Admin">Admin</option>
                  <option value="Viewer">Viewer</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Brand Access</label>
                <select
                  value={editingUser.brand_id}
                  onChange={(e) => setEditingUser({...editingUser, brand_id: parseInt(e.target.value)})}
                  className="premium-select"
                  style={styles.modalInput}
                >
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Status</label>
                <div style={styles.statusToggle}>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="userStatus"
                      checked={editingUser.is_active === true}
                      onChange={() => setEditingUser({...editingUser, is_active: true})}
                    />
                    <span style={{ color: '#34C759' }}>Active</span>
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="userStatus"
                      checked={editingUser.is_active === false}
                      onChange={() => setEditingUser({...editingUser, is_active: false})}
                    />
                    <span style={{ color: '#FF3B30' }}>Inactive</span>
                  </label>
                </div>
              </div>
            </div>

            <div style={styles.modalActions}>
              <button 
                onClick={handleUpdateUser}
                className="premium-button success"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button 
                onClick={() => setEditingUser(null)}
                className="premium-button secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '24px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px'
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: 'var(--system-label)',
    margin: 0
  },
  createButton: {
    padding: '12px 24px',
    fontSize: '15px'
  },
  formCard: {
    marginBottom: '30px',
    padding: '24px',
    animation: 'slideDown 0.3s ease'
  },
  formTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '20px',
    color: 'var(--system-label)'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--system-secondary-label)'
  },
  input: {
    height: '44px'
  },
  checkboxGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    height: '44px'
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer'
  },
  checkboxLabel: {
    fontSize: '14px',
    color: 'var(--system-label)'
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px'
  },
  tableCard: {
    marginTop: '20px'
  },
  tableTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '20px',
    color: 'var(--system-label)'
  },
  table: {
    minWidth: '1000px'
  },
  tableRow: {
    transition: 'all 0.2s ease'
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  roleBadge: {
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
    display: 'inline-block'
  },
  brandBadge: {
    padding: '4px 8px',
    background: 'var(--system-fill)',
    borderRadius: '12px',
    fontSize: '12px',
    color: 'var(--system-label)',
    display: 'inline-block'
  },
  statusBadge: {
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
    display: 'inline-block'
  },
  actionButtons: {
    display: 'flex',
    gap: '6px'
  },
  actionButton: {
    padding: '6px 10px',
    fontSize: '14px',
    minWidth: '36px'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(5px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease'
  },
  modal: {
    width: '90%',
    maxWidth: '500px',
    padding: '30px',
    animation: 'slideUp 0.3s ease'
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '24px',
    color: 'var(--system-label)'
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    marginBottom: '30px'
  },
  modalInput: {
    height: '44px',
    width: '100%'
  },
  statusToggle: {
    display: 'flex',
    gap: '20px',
    padding: '8px 0'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end'
  }
};

// Add keyframes
const style = document.createElement('style');
style.textContent = `
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;
document.head.appendChild(style);

export default UserManagement;